import React, { useEffect, useMemo, useState } from "react";

import { fetchChartOfAccounts, postGlBatch } from "../../api/glApi";

const createLine = () => ({
  accountId: "",
  action: "",
  amount: "",
  memo: "",
});

const todayIso = () => new Date().toISOString().slice(0, 10);

const JournalLinesForm = ({ onPosted }) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [header, setHeader] = useState(() => ({
    entryDate: todayIso(),
    reference: "",
    description: "",
  }));
  const [lines, setLines] = useState([createLine(), createLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        setLoadingAccounts(true);
        setLoadError("");
        const data = await fetchChartOfAccounts();
        if (!ignore) {
          setAccounts(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!ignore) {
          setLoadError(err.message || "Unable to load chart of accounts");
        }
      } finally {
        if (!ignore) setLoadingAccounts(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, []);

  const handleHeaderChange = (event) => {
    const { name, value } = event.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    setLines((prev) =>
      prev.map((line, idx) => (idx === index ? { ...line, [field]: value } : line))
    );
  };

  const addLine = () => setLines((prev) => [...prev, createLine()]);
  const removeLine = (index) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev));

  const accountTree = useMemo(() => {
    const map = new Map();
    const childMap = new Map();
    accounts.forEach((acct) => {
      map.set(acct.id, acct);
      if (acct.parent_id) {
        if (!childMap.has(acct.parent_id)) childMap.set(acct.parent_id, []);
        childMap.get(acct.parent_id).push(acct);
      }
    });

    const buildPath = (acct) => {
      const chain = [];
      let current = acct;
      const visited = new Set();
      while (current && !visited.has(current.id)) {
        visited.add(current.id);
        chain.unshift(current);
        current = current.parent_id ? map.get(current.parent_id) : null;
      }
      return chain;
    };

    const describe = (accountId) => {
      const acct = map.get(Number(accountId));
      if (!acct) return "";
      const path = buildPath(acct);
      return `${path.map((node) => node.code || node.name).join(" / ")} -> ${acct.name}`;
    };

    const rootName = (accountId) => {
      const acct = map.get(Number(accountId));
      if (!acct) return "";
      const root = buildPath(acct)[0];
      return root ? `${root.code || ""} ${root.name}`.trim() : "";
    };

    const postingAccounts = accounts
      .filter((acct) => !childMap.has(acct.id))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

    return { describe, rootName, postingAccounts };
  }, [accounts]);

  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        const amount = parseFloat(line.amount) || 0;
        if (line.action === "debit") {
          return { ...acc, debit: acc.debit + amount };
        }
        if (line.action === "credit") {
          return { ...acc, credit: acc.credit + amount };
        }
        return acc;
      },
      { debit: 0, credit: 0 }
    );
  }, [lines]);

  const difference = useMemo(() => totals.debit - totals.credit, [totals]);

  const debitPreview = useMemo(
    () =>
      lines
        .map((line) => ({ line, amount: parseFloat(line.amount) || 0 }))
        .filter((entry) => entry.amount > 0 && entry.line.action === "debit"),
    [lines]
  );

  const creditPreview = useMemo(
    () =>
      lines
        .map((line) => ({ line, amount: parseFloat(line.amount) || 0 }))
        .filter((entry) => entry.amount > 0 && entry.line.action === "credit"),
    [lines]
  );

  const validate = () => {
    if (lines.length < 2) return "At least two journal lines are required.";
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.accountId) return `Line ${i + 1}: please select an account.`;
      const amount = parseFloat(line.amount) || 0;
      if (!line.action) return `Line ${i + 1}: choose Debit or Credit.`;
      if (line.action !== "debit" && line.action !== "credit") {
        return `Line ${i + 1}: action must be Debit or Credit.`;
      }
      if (amount <= 0) {
        return `Line ${i + 1}: amount must be greater than zero.`;
      }
    }
    if (totals.debit <= 0 || totals.credit <= 0) {
      return "Provide at least one debit and one credit line.";
    }
    if (Math.abs(difference) > 0.005) {
      return "Debits and credits must balance.";
    }
    return "";
  };

  const resetForm = () => {
    setHeader({
      entryDate: todayIso(),
      reference: "",
      description: "",
    });
    setLines([createLine(), createLine()]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationMessage = validate();
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }
    try {
      setSubmitting(true);
      setFormError("");
      setStatusMessage("");

      const payload = {
        entry_date: header.entryDate || null,
        description: header.description || "Manual journal entry",
        reference_no: header.reference || null,
        lines: lines.map((line) => ({
          account_id: Number(line.accountId),
          direction: line.action,
          amount: line.amount ? line.amount : "0",
          note: line.memo || null,
        })),
      };

      const response = await postGlBatch(payload);
      setStatusMessage(response?.message || "Journal batch posted successfully.");
      resetForm();
      if (typeof onPosted === "function") onPosted();
    } catch (err) {
      setFormError(err.message || "Failed to post journal batch.");
    } finally {
      setSubmitting(false);
    }
  };

  const disableSubmit = Boolean(validate());

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
      <div className="flex flex-col gap-2 border-b border-slate-100 px-6 py-4">
        <h2 className="text-lg font-semibold text-slate-900">Journal lines</h2>
        <p className="text-sm text-slate-500">
          Post balanced journal lines straight to the general ledger. Pick an account from the chart
          and mark each line as a debit or credit.
        </p>
        {loadError ? <p className="text-sm text-red-600">{loadError}</p> : null}
        {loadingAccounts ? (
          <p className="text-sm text-slate-500">Loading chart of accounts...</p>
        ) : null}
        {statusMessage ? (
          <p className="text-sm font-medium text-emerald-700">{statusMessage}</p>
        ) : null}
        {formError ? <p className="text-sm text-red-600">{formError}</p> : null}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Transaction date</label>
            <input
              type="date"
              name="entryDate"
              value={header.entryDate}
              onChange={handleHeaderChange}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Reference</label>
            <input
              type="text"
              name="reference"
              value={header.reference}
              onChange={handleHeaderChange}
              placeholder="Optional reference"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Narration</label>
            <input
              type="text"
              name="description"
              value={header.description}
              onChange={handleHeaderChange}
              placeholder="What is this journal for?"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Journal lines</h3>
              <p className="text-xs text-slate-500">Account, action, amount, memo, actions.</p>
            </div>
            <button
              type="button"
              onClick={addLine}
              className="rounded-lg border border-emerald-200 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50"
            >
              Add line
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Account</th>
                  <th className="px-3 py-2 text-left font-medium">Account action</th>
                  <th className="px-3 py-2 text-left font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Memo</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.map((line, index) => (
                  <tr key={`line-${index}`}>
                    <td className="px-3 py-2">
                      <select
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        value={line.accountId}
                        disabled={loadingAccounts || accountTree.postingAccounts.length === 0}
                        onChange={(event) =>
                          handleLineChange(index, "accountId", event.target.value)
                        }
                      >
                        <option value="">Select account</option>
                        {accountTree.postingAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.code} - {account.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-slate-500">
                        {accountTree.rootName(line.accountId) || "Choose a chart code"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        value={line.action}
                        onChange={(event) => handleLineChange(index, "action", event.target.value)}
                      >
                        <option value="">Select action</option>
                        <option value="debit">Debit</option>
                        <option value="credit">Credit</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.amount}
                        onChange={(event) => handleLineChange(index, "amount", event.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        placeholder="0.00"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.memo}
                        onChange={(event) => handleLineChange(index, "memo", event.target.value)}
                        placeholder="Optional memo"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                        disabled={lines.length <= 2}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 text-sm font-semibold text-slate-700">
                  <td className="px-3 py-2 text-right" colSpan={2}>
                    Totals:
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span>Debit</span>
                      <span>{totals.debit.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Credit</span>
                      <span>{totals.credit.toFixed(2)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2" colSpan={2}>
                    Difference:{" "}
                    <span
                      className={Math.abs(difference) > 0.005 ? "text-red-600" : "text-emerald-700"}
                    >
                      {difference.toFixed(2)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 p-4 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">Posting preview</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Debit accounts
              </p>
              {debitPreview.length === 0 ? (
                <p className="text-sm text-slate-500">No debits yet.</p>
              ) : (
                <ul className="space-y-2 text-sm text-slate-700">
                  {debitPreview.map(({ line, amount }, idx) => (
                    <li key={`debit-preview-${idx}`}>
                      <div className="font-medium">
                        {accountTree.describe(line.accountId) || "Select account"}
                      </div>
                      <div className="text-slate-500">
                        {amount.toFixed(2)} NGN {line.memo ? `- ${line.memo}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Credit accounts
              </p>
              {creditPreview.length === 0 ? (
                <p className="text-sm text-slate-500">No credits yet.</p>
              ) : (
                <ul className="space-y-2 text-sm text-slate-700">
                  {creditPreview.map(({ line, amount }, idx) => (
                    <li key={`credit-preview-${idx}`}>
                      <div className="font-medium">
                        {accountTree.describe(line.accountId) || "Select account"}
                      </div>
                      <div className="text-slate-500">
                        {amount.toFixed(2)} NGN {line.memo ? `- ${line.memo}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || disableSubmit}
          className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {submitting ? "Posting..." : "Post journal batch"}
        </button>
      </form>
    </section>
  );
};

export default JournalLinesForm;
